import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { peopleGateway } from '../../people';
import type { CompanyWorkLocationOption } from '../../people/types';
import { CompanyRatesSection } from '../components/CompanyRatesSection';
import { CompensationSetupReviewPanel } from '../components/CompensationSetupReviewPanel';
import { IndustryProfileSection } from '../components/IndustryProfileSection';
import { ServiceTypesSection } from '../components/ServiceTypesSection';
import { WorkerCategoriesSection } from '../components/WorkerCategoriesSection';
import { healthcareErrorMessage, isForbidden } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import {
  COMPENSATION_INTRO,
  HEALTHCARE_REIMBURSEMENT_NOTE,
  SYNTHETIC_RATES_NOTICE,
  SYNTHETIC_RATES_NOTICE_TITLE,
  defaultTagsFor,
  matchesSuggestion,
  primaryIndustryLabel,
  type CompanyIndustryProfile,
  type Discipline,
  type ServiceType,
} from '../types';
import { useHealthcareCapabilities } from '../useHealthcareCapabilities';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: CompanyIndustryProfile; serviceTypes: ServiceType[] }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

/**
 * Compensation rates (Settings, Payroll): the standard ways the company pays workers, by worker category and by kind of work, for any industry. Four parts:
 * the company industry (a suggestion only), worker categories, work types and the company rates. EPN pays the worker under the employer's own compensation
 * agreement. The industry template never sets pay, restricts who the company may pay, or decides a legal classification. Healthcare reimbursement guidance
 * appears only for a company in Health Care and Social Assistance. The server decides every permission; what is hidden here is only what the capability
 * reads say the person cannot do.
 */
export function CompensationRatesPage() {
  const caps = useHealthcareCapabilities();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [disciplines, setDisciplines] = useState<Discipline[] | null>(null);
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);
  const [activeRates, setActiveRates] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  const [requests, setRequests] = useState({ category: 0, type: 0, rate: 0 });
  const synthetic = healthcareRatesGateway.isSynthetic;

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      healthcareRatesGateway.getIndustryProfile(controller.signal),
      healthcareRatesGateway.listServiceTypes({ includeInactive: true }, controller.signal),
    ])
      .then(([profile, serviceTypes]) => { if (!controller.signal.aborted) setLoad({ kind: 'ready', profile, serviceTypes }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoad(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: healthcareErrorMessage(err, 'Could not load the compensation settings.') });
      });
    return () => controller.abort();
  }, [nonce]);

  // The worker categories and the locations fill their own section and the rate pickers; a failed read leaves them short and the rest of the page works.
  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway.listDisciplines({ includeInactive: true }, controller.signal).then((d) => { if (!controller.signal.aborted) setDisciplines(d); }).catch(() => undefined);
    peopleGateway.listCompanyWorkLocations().then((l) => { if (!controller.signal.aborted) setLocations(l.filter((x) => x.status === 'Active')); }).catch(() => undefined);
    return () => controller.abort();
  }, [nonce]);

  const ready = load.kind === 'ready' ? load : null;
  const tags = useMemo(() => ready?.profile.suggestionTags ?? [], [ready]);
  const defaultTags = useMemo(() => defaultTagsFor(ready?.profile ?? null), [ready]);
  const industryLabel = primaryIndustryLabel(ready?.profile ?? null);
  const healthcare = ready?.profile.isHealthcare ?? false;

  const disciplineOptions = useMemo(() => (disciplines ?? []).filter((d) => d.isActive).map((d) => ({ id: d.id, name: d.name })), [disciplines]);
  const locationOptions = useMemo(() => locations.map((l) => ({ id: l.id, name: l.name })), [locations]);
  const canManageIndustry = caps.serviceType?.canManageServiceTypes !== false;
  const canManageTypes = caps.serviceType?.canManageServiceTypes !== false;
  const canManageRates = caps.serviceType?.canManageCompanyRates !== false;
  const canManageCategories = caps.discipline?.canManageDisciplines !== false;

  // What is available to the company: its own active rows, and EPN's active defaults that are suggested for its industry (all of them when no industry is chosen).
  const availableCount = (rows: Array<{ isActive: boolean; isSystem: boolean; industries: string[] }>) =>
    rows.filter((r) => r.isActive && (!r.isSystem || tags.length === 0 || matchesSuggestion(r.industries, tags))).length;

  const ask = (key: 'category' | 'type' | 'rate', sectionId: string) => { setRequests((r) => ({ ...r, [key]: r[key] + 1 })); scrollTo(sectionId); };

  return (
    <section className="card card--wide jr-page hdc-page">
      <div className="section-header">
        <h2>Compensation Rates</h2>
      </div>
      <p className="muted jr-explainer">{COMPENSATION_INTRO}</p>
      {healthcare && <p className="muted jr-explainer" data-testid="healthcare-reimbursement-note">{HEALTHCARE_REIMBURSEMENT_NOTE}</p>}
      {synthetic && (
        <div className="hdc-synthetic" role="note" data-testid="synthetic-notice">
          <strong>{SYNTHETIC_RATES_NOTICE_TITLE}</strong>
          {SYNTHETIC_RATES_NOTICE}
        </div>
      )}

      {load.kind === 'loading' && <p className="muted" role="status">Loading compensation settings…</p>}
      {load.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view pay rates (workrates.read)" />}
      {load.kind === 'error' && <RetryableError message={load.message} onRetry={reload} />}
      {ready && (
        <>
          <ul className="hdc-summary" aria-label="Summary" data-testid="compensation-summary">
            <li><span className="hdc-summary__label">Industry</span><strong>{industryLabel ?? (ready.profile.needsReview ? 'Needs review' : 'Not set')}</strong></li>
            <li><span className="hdc-summary__label">Worker categories</span><strong>{disciplines ? `${availableCount(disciplines)} active` : '…'}</strong></li>
            <li><span className="hdc-summary__label">Work types</span><strong>{availableCount(ready.serviceTypes)} active</strong></li>
            <li><span className="hdc-summary__label">Company rates</span><strong>{activeRates === null ? '…' : `${activeRates} active`}</strong></li>
          </ul>

          <div className="hdc-actions" role="group" aria-label="Add">
            {canManageCategories && <button type="button" className="jr-btn jr-btn--primary" onClick={() => ask('category', 'worker-categories')}>Add Worker Category</button>}
            {canManageTypes && <button type="button" className="jr-btn jr-btn--primary" onClick={() => ask('type', 'work-types')}>Add Work Type</button>}
            {canManageRates && <button type="button" className="jr-btn jr-btn--primary" onClick={() => ask('rate', 'company-rates')}>Add Company Rate</button>}
          </div>

          <IndustryProfileSection
            profile={ready.profile}
            canManage={canManageIndustry}
            onSaved={(profile) => setLoad((prev) => (prev.kind === 'ready' ? { ...prev, profile } : prev))}
            onReload={reload}
            catalogs={{ serviceTypes: ready.serviceTypes, disciplines }}
          />
          <CompensationSetupReviewPanel />
          <WorkerCategoriesSection
            key={`wc-${ready.profile.concurrencyVersion}`}
            categories={disciplines ?? []}
            tags={tags}
            industryLabel={industryLabel}
            defaultTags={defaultTags}
            healthcare={healthcare}
            canManage={canManageCategories}
            onChanged={reload}
            addRequest={requests.category}
          />
          <ServiceTypesSection
            key={`wt-${ready.profile.concurrencyVersion}`}
            serviceTypes={ready.serviceTypes}
            tags={tags}
            industryLabel={industryLabel}
            defaultTags={defaultTags}
            healthcare={healthcare}
            canManage={canManageTypes}
            onChanged={reload}
            addRequest={requests.type}
          />
          <CompanyRatesSection
            disciplines={disciplineOptions}
            serviceTypes={ready.serviceTypes}
            locations={locationOptions}
            canManage={canManageRates}
            addRequest={requests.rate}
            onActiveCount={setActiveRates}
          />
        </>
      )}
    </section>
  );
}
